import { useEffect, useLayoutEffect, useRef, useState } from 'react';
/** Translation is an explicit draft action. Sending never depends on a provider. */
export function useTranslationPreview(api, roomId, scope, body, enabled, onBodyChange) {
    const key = JSON.stringify([roomId, scope, body, enabled]);
    const [config, setConfig] = useState();
    const [result, setResult] = useState();
    const [applied, setApplied] = useState({});
    const request = useRef(undefined);
    const latest = useRef(key);
    const generation = useRef(0);
    const languageScope = JSON.stringify([roomId, scope, enabled]);
    const language = config?.scope === languageScope ? config.value.language : 'en';
    const override = config?.scope === languageScope ? config.value.override : '';
    const current = result?.key === key ? result : undefined;
    const originalBody = applied[scope]?.body === body ? applied[scope].original : undefined;
    const canTranslate = enabled && Boolean(roomId) && language === 'en' && /\p{Script=Han}/u.test(body);
    function cancel() { generation.current += 1; request.current?.abort(); request.current = undefined; setResult(undefined); }
    useLayoutEffect(() => {
        latest.current = key;
        return () => { generation.current += 1; request.current?.abort(); };
    }, [key]);
    useEffect(() => {
        if (!enabled || !roomId)
            return;
        const abort = new AbortController();
        const read = () => api(`/conversations/${roomId}/language`, 'GET', undefined, abort.signal)
            .then(value => { if (!abort.signal.aborted)
            setConfig({ scope: languageScope, value }); })
            .catch(() => { });
        void read();
        return () => { abort.abort(); };
    }, [api, roomId, enabled, languageScope]);
    async function retry() {
        if (!canTranslate)
            return;
        cancel();
        const revision = generation.current;
        const abort = new AbortController();
        request.current = abort;
        setResult({ key, busy: true });
        try {
            const value = await api(`/conversations/${roomId}/translate`, 'POST', { body }, abort.signal);
            if (abort.signal.aborted || latest.current !== key || generation.current !== revision)
                return;
            if (!value.body?.trim() || value.body.length > 10000)
                throw new Error('Invalid translation');
            setResult({ key, preview: { original: body, body: value.body } });
        }
        catch {
            if (!abort.signal.aborted && latest.current === key && generation.current === revision)
                setResult({ key, error: 'Translation unavailable. You can send the original text. Click to retry.' });
        }
    }
    function changeBody(value) {
        cancel();
        setResult(undefined);
        if (originalBody)
            setApplied(previous => ({ ...previous, [scope]: { original: originalBody, body: value } }));
        onBodyChange(value);
    }
    function apply() {
        if (!current?.preview?.body.trim())
            return;
        cancel();
        setApplied(previous => ({ ...previous, [scope]: current.preview }));
        onBodyChange(current.preview.body);
        setResult(undefined);
    }
    function clear() {
        cancel();
        setResult(undefined);
        setApplied(previous => { const next = { ...previous }; delete next[scope]; return next; });
    }
    function useOriginal() { if (originalBody)
        onBodyChange(originalBody); clear(); }
    async function changeLanguage(value) {
        if (!roomId)
            return;
        const snapshot = latest.current;
        cancel();
        setResult(undefined);
        const abort = new AbortController();
        request.current = abort;
        try {
            const response = await api(`/conversations/${roomId}/language`, 'PUT', { language: value }, abort.signal);
            if (!abort.signal.aborted && latest.current === snapshot)
                setConfig({ scope: languageScope, value: response });
        }
        catch {
            if (!abort.signal.aborted && latest.current === snapshot)
                setResult({ key, error: 'Unable to save channel language. Your draft is kept.' });
        }
    }
    return {
        enabled, key, language, override, canTranslate, preview: current?.preview, busy: Boolean(current?.busy), error: current?.error || '',
        unavailable: config?.scope === languageScope && config.value.translation_available === false,
        ready: true, outgoing: body, originalBody, originalOnly: !originalBody,
        edit: (text) => setResult(previous => previous?.key === key && previous.preview ? { ...previous, preview: { ...previous.preview, body: text } } : previous),
        retry, apply, useOriginal, changeLanguage, changeBody, clear, cancel,
    };
}
